import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

export default function Marketplace() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', q: '' });

  useEffect(() => {
    setLoading(true);
    api.get('/listings', { params: filter })
      .then((r) => {
        setListings(r.data.listings || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filter]);

  const typeColors = {
    individual: 'var(--neon-green)',
    organization: 'var(--neon-cyan)',
    compliance_entity: 'var(--neon-orange)',
  };

  const typeLabels = {
    individual: 'Individual seller',
    organization: 'Organization',
    compliance_entity: 'Compliance entity',
  };

  const filterPills = useMemo(() => ([
    ['', 'All sellers'],
    ['individual', 'Individuals'],
    ['organization', 'Organizations'],
    ['compliance_entity', 'Compliance entities'],
  ]), []);

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Verified listings</div>
        <h1 className="section-title">Marketplace redesigned for faster scanning and cleaner buying decisions</h1>
        <p className="section-copy">
          Seller type, project information, price, availability, and verification data are now surfaced in a more structured card system. The goal is to help a buyer decide faster without visual clutter.
        </p>
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="stat-label">Why this feels better</div>
          <div className="spotlight-value" style={{ fontSize: '2rem', marginTop: '0.6rem', color: 'var(--neon-cyan)' }}>Scan, compare, move</div>
          <p className="spotlight-copy" style={{ marginTop: '0.8rem' }}>
            The listing cards are flatter, cleaner, and more consumer-grade in their information hierarchy. This is where the Zomato, Swiggy, and Zepto influence is useful: reduce friction, make the choice architecture obvious, keep the page quick.
          </p>
        </div>

        <div className="duo-grid">
          <div className="surface-panel stat-card">
            <div className="stat-label">Active listings</div>
            <div className="stat-value" style={{ color: 'var(--neon-green)' }}>{loading ? '...' : listings.length}</div>
          </div>
          <div className="surface-panel stat-card">
            <div className="stat-label">Seller filters</div>
            <div className="stat-value" style={{ color: 'var(--neon-orange)' }}>4</div>
          </div>
        </div>
      </section>

      <section className="surface-panel filter-shell">
        <div className="filter-pills">
          {filterPills.map(([value, label]) => (
            <button
              key={value || 'all'}
              type="button"
              className={`filter-pill ${filter.type === value ? 'active' : ''}`}
              onClick={() => setFilter((prev) => ({ ...prev, type: value }))}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="search-shell">
          <input
            className="form-input"
            type="text"
            placeholder="Search by seller or project"
            value={filter.q}
            onChange={(e) => setFilter((prev) => ({ ...prev, q: e.target.value }))}
          />
        </div>
      </section>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <section className="listing-grid">
          {listings.length === 0 && (
            <div className="surface-panel" style={{ padding: '2rem', gridColumn: '1/-1' }}>
              <div className="section-stack">
                <div className="feature-title">No listings found</div>
                <div className="stat-sub">Try a different seller filter or refine the search term.</div>
              </div>
            </div>
          )}

          {listings.map((listing) => {
            const color = typeColors[listing.seller_type] || 'var(--neon-green)';

            return (
              <div key={listing.id} className="surface-panel listing-card glow-surface">
                <div className="listing-top">
                  <div className="section-stack" style={{ gap: '0.35rem' }}>
                    <span className="badge" style={{ color, borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}>
                      {typeLabels[listing.seller_type]}
                    </span>
                    <div className="listing-title">{listing.seller_name}</div>
                    <div className="listing-copy">{listing.project}</div>
                  </div>
                  <div className="price-block" style={{ alignItems: 'flex-end' }}>
                    <div className="price-value" style={{ color }}>₹{listing.price_per_unit.toLocaleString('en-IN')}</div>
                    <div className="price-caption">Per tCO2e</div>
                  </div>
                </div>

                <div className="listing-meta-grid">
                  <div>
                    <div className="meta-label">Volume</div>
                    <div className="meta-value">{listing.co2_equivalent}</div>
                  </div>
                  <div>
                    <div className="meta-label">Available</div>
                    <div className="meta-value">{listing.qty_available.toLocaleString('en-IN')} units</div>
                  </div>
                  <div>
                    <div className="meta-label">Vintage</div>
                    <div className="meta-value">{listing.vintage}</div>
                  </div>
                  <div>
                    <div className="meta-label">Verified by</div>
                    <div className="meta-value">{listing.verified_by}</div>
                  </div>
                </div>

                {listing.sdg_goals?.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                    {listing.sdg_goals.map((goal) => (
                      <span key={goal} className="badge badge-muted">{goal}</span>
                    ))}
                  </div>
                )}

                <div className="listing-footer">
                  <div className="section-stack" style={{ gap: '0.35rem' }}>
                    {listing.trust_level && (
                      <span className="badge" style={{
                        color: listing.trust_level === 'verified' ? 'var(--neon-green)' : listing.trust_level === 'docs_checked' ? 'var(--neon-cyan)' : 'var(--neon-yellow)',
                        borderColor: 'transparent', background: 'rgba(255,255,255,0.04)', fontSize: '0.6rem',
                      }}>
                        {listing.trust_level === 'verified' ? '✓ Verified' : listing.trust_level === 'docs_checked' ? '✓ Docs Checked' : '○ Self-reported'}
                      </span>
                    )}
                    <div className="stat-sub" style={{ fontSize: '0.75rem', color: 'var(--muted2)' }}>Information only. Conduct your own due diligence.</div>
                  </div>
                  <Link to={`/listings/${listing.id}`} className="btn btn-secondary">View →</Link>
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
